import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  connectAuthEmulator,
  type Auth,
} from "firebase/auth";
import { getFirestore, connectFirestoreEmulator, type Firestore } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator, type Functions } from "firebase/functions";
import {
  getRemoteConfig,
  fetchAndActivate,
  getString,
  type RemoteConfig,
} from "firebase/remote-config";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import type { LanguageCode } from "./types";

// Allow toggling the App Check debug token on the window before init.
declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window {
    FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean;
  }
}

const env = import.meta.env;

function required(key: keyof ImportMetaEnv): string {
  const v = env[key];
  if (!v) {
    throw new Error(
      `Missing required env var ${String(key)}. Add it to .env.local.`
    );
  }
  return v as string;
}

const firebaseConfig = {
  apiKey: required("VITE_FIREBASE_API_KEY"),
  authDomain: required("VITE_FIREBASE_AUTH_DOMAIN"),
  projectId: required("VITE_FIREBASE_PROJECT_ID"),
  storageBucket: required("VITE_FIREBASE_STORAGE_BUCKET"),
  messagingSenderId: required("VITE_FIREBASE_MESSAGING_SENDER_ID"),
  appId: required("VITE_FIREBASE_APP_ID"),
};

export const app: FirebaseApp = initializeApp(firebaseConfig);

// Activate App Check (required because the test project enforces it).
const debugToken = env.VITE_FIREBASE_APP_CHECK_DEBUG_TOKEN || (env.DEV ? true : undefined);
if (debugToken && typeof window !== "undefined") {
  // Setting this before initializeAppCheck enables debug-token mode in dev.
  window.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
}

try {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(required("VITE_RECAPTCHA_SITE_KEY")),
    isTokenAutoRefreshEnabled: true,
  });
} catch (err) {
  // Avoid crashing the app if App Check fails to init in odd environments.
  // eslint-disable-next-line no-console
  console.warn("App Check initialization failed:", err);
}

export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(
  app,
  env.VITE_FIRESTORE_DATABASE_ID || "(default)"
);
export const functions: Functions = getFunctions(app);

if (env.VITE_USE_EMULATOR === "true") {
  connectAuthEmulator(auth, "http://localhost:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "localhost", 8080);
  connectFunctionsEmulator(functions, "localhost", 5001);
}

export const googleProvider = new GoogleAuthProvider();

// ---------------------------------------------------------------------------
// Remote Config
// ---------------------------------------------------------------------------
export const remoteConfig: RemoteConfig = getRemoteConfig(app);
remoteConfig.settings.minimumFetchIntervalMillis = env.DEV ? 0 : 3600000; // 0 in dev, 1h in prod

/** Fetch and activate Remote Config values. Call once at app startup. */
export const activateRemoteConfig = (): Promise<boolean> =>
  fetchAndActivate(remoteConfig);

/**
 * Retrieve localized device name and description from Remote Config.
 *
 * Uses the parameter pattern: device_{deviceType}_{language}_name
 *                              device_{deviceType}_{language}_description
 */
export function getDeviceInfo(
  deviceType: string,
  language: LanguageCode
): { name: string; description: string } {
  const prefix = `device_${deviceType}_${language}`;
  return {
    name: getString(remoteConfig, `${prefix}_name`),
    description: getString(remoteConfig, `${prefix}_description`),
  };
}
